// ComplianceMakerDashboard.js - Compliance Maker Role Dashboard
// URL-path-based tabs following BankFXPartnerDashboard pattern
// Focus: KYC first-leg review, CDS signing, screening adjudication, documents, audit trail

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Shield, CheckCircle, XCircle, Clock, Eye, FileText, Search,
  Filter, RefreshCw, UserCheck, Flag, AlertTriangle, Download,
  ArrowUpRight, ArrowDownRight, Upload, Tag, BarChart3, Edit3,
  FileCheck, ClipboardList, History, ShieldCheck, PenTool, Users,
  AlertCircle, MessageSquare, Inbox
} from 'lucide-react';
import { getStatusStyle } from '../../config/statusColors';
import { useAuth } from '../../context/AuthContext';

// CONSTANTS
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// DEMO / FALLBACK DATA
const demoData = {
  metrics: {
    pendingReviews: 14,
    approvedToday: 6,
    rejectedToday: 1,
    avgReviewTime: '2.4h',
    pendingCds: 5,
    screeningAlerts: 8,
    documentsToReview: 11,
  },
  kycQueue: [
    { id: 'KYC-1041', investorName: 'Horizon Impact Partners', submissionDate: '2026-09-10T08:30:00Z', riskLevel: 'low', status: 'pending', type: 'Individual', country: 'KE' },
    { id: 'KYC-1042', investorName: 'Atlas Growth Capital', submissionDate: '2026-09-10T09:15:00Z', riskLevel: 'high', status: 'pending', type: 'Corporate', country: 'NG' },
    { id: 'KYC-1043', investorName: 'Nordic Impact Fund', submissionDate: '2026-09-09T14:00:00Z', riskLevel: 'moderate', status: 'pending', type: 'Fund', country: 'SE' },
    { id: 'KYC-1044', investorName: 'Geneva Wealth Management', submissionDate: '2026-09-09T11:20:00Z', riskLevel: 'low', status: 'kyc_maker_approved', type: 'Corporate', country: 'CH' },
    { id: 'KYC-1045', investorName: 'Sahel Capital Advisors', submissionDate: '2026-09-08T16:45:00Z', riskLevel: 'high', status: 'rejected', type: 'Individual', country: 'SN' },
    { id: 'KYC-1046', investorName: 'East Africa Ventures', submissionDate: '2026-09-08T10:00:00Z', riskLevel: 'moderate', status: 'pending', type: 'Corporate', country: 'TZ' },
    { id: 'KYC-1047', investorName: 'Pacific Rim Investments', submissionDate: '2026-09-07T13:30:00Z', riskLevel: 'low', status: 'kyc_maker_approved', type: 'Fund', country: 'SG' },
  ],
  cdsCards: [
    { id: 'CDS-301', investorName: 'Horizon Impact Partners', bank: 'Standard Bank', cardStatus: 'cds_card_checker_signed', firstSigner: 'Jane Wambui', firstSignedAt: '2026-09-10T10:00:00Z', accountNumber: 'SB-10045821', csdRef: 'CSD-KE-00412' },
    { id: 'CDS-302', investorName: 'Atlas Growth Capital', bank: 'Equity Bank', cardStatus: 'cds_card_pending', firstSigner: null, firstSignedAt: null, accountNumber: 'EQ-20031847', csdRef: 'CSD-KE-00413' },
    { id: 'CDS-303', investorName: 'Geneva Wealth Management', bank: 'Standard Bank', cardStatus: 'cds_card_checker_signed', firstSigner: 'James Oduor', firstSignedAt: '2026-09-09T15:30:00Z', accountNumber: 'SB-10045830', csdRef: 'CSD-KE-00414' },
    { id: 'CDS-304', investorName: 'Nordic Impact Fund', bank: 'KCB Bank', cardStatus: 'cds_card_complete', firstSigner: 'Jane Wambui', firstSignedAt: '2026-09-08T09:00:00Z', accountNumber: 'KCB-30092847', csdRef: 'CSD-KE-00415' },
    { id: 'CDS-305', investorName: 'East Africa Ventures', bank: 'Equity Bank', cardStatus: 'cds_card_checker_signed', firstSigner: 'James Oduor', firstSignedAt: '2026-09-10T11:45:00Z', accountNumber: 'EQ-20031860', csdRef: 'CSD-KE-00416' },
  ],
  screeningAlerts: [
    { id: 'SCR-501', investorName: 'Atlas Growth Capital', type: 'PEP', matchScore: 92, status: 'pending', matchedEntity: 'Atlas Growth Corp (Nigeria)', source: 'World-Check', detectedAt: '2026-09-10T08:00:00Z' },
    { id: 'SCR-502', investorName: 'Sahel Capital Advisors', type: 'Sanctions', matchScore: 78, status: 'flagged', matchedEntity: 'Sahel Trading Ltd', source: 'OFAC SDN', detectedAt: '2026-09-09T14:30:00Z' },
    { id: 'SCR-503', investorName: 'Blue Nile Holdings', type: 'Adverse Media', matchScore: 65, status: 'pending', matchedEntity: 'Blue Nile Corp', source: 'Dow Jones', detectedAt: '2026-09-09T10:15:00Z' },
    { id: 'SCR-504', investorName: 'Pacific Rim Investments', type: 'PEP', matchScore: 45, status: 'cleared', matchedEntity: 'Pacific Rim Ltd (SG)', source: 'World-Check', detectedAt: '2026-09-08T16:00:00Z' },
    { id: 'SCR-505', investorName: 'East Africa Ventures', type: 'Sanctions', matchScore: 88, status: 'escalated', matchedEntity: 'EA Ventures Trading', source: 'EU Sanctions List', detectedAt: '2026-09-08T11:20:00Z' },
    { id: 'SCR-506', investorName: 'Geneva Wealth Management', type: 'Adverse Media', matchScore: 32, status: 'cleared', matchedEntity: 'Geneva WM AG', source: 'LexisNexis', detectedAt: '2026-09-07T09:45:00Z' },
    { id: 'SCR-507', investorName: 'Horizon Impact Partners', type: 'PEP', matchScore: 55, status: 'pending', matchedEntity: 'Horizon Partners KE', source: 'World-Check', detectedAt: '2026-09-10T07:30:00Z' },
  ],
  documents: [
    { id: 'DOC-701', name: 'Passport - John Kariuki', type: 'ID Document', uploadedBy: 'Horizon Impact Partners', date: '2026-09-10T08:00:00Z', status: 'pending', size: '2.4 MB', caseRef: 'KYC-1041' },
    { id: 'DOC-702', name: 'Certificate of Incorporation', type: 'Corporate Document', uploadedBy: 'Atlas Growth Capital', date: '2026-09-10T09:00:00Z', status: 'approved', size: '1.8 MB', caseRef: 'KYC-1042' },
    { id: 'DOC-703', name: 'Bank Statement - Q2 2026', type: 'Financial Document', uploadedBy: 'Nordic Impact Fund', date: '2026-09-09T14:30:00Z', status: 'pending', size: '3.1 MB', caseRef: 'KYC-1043' },
    { id: 'DOC-704', name: 'Board Resolution', type: 'Corporate Document', uploadedBy: 'Geneva Wealth Management', date: '2026-09-09T10:00:00Z', status: 'approved', size: '0.9 MB', caseRef: 'KYC-1044' },
    { id: 'DOC-705', name: 'Source of Funds Declaration', type: 'Compliance Document', uploadedBy: 'Sahel Capital Advisors', date: '2026-09-08T15:00:00Z', status: 'rejected', size: '1.2 MB', caseRef: 'KYC-1045' },
    { id: 'DOC-706', name: 'Tax Clearance Certificate', type: 'Tax Document', uploadedBy: 'East Africa Ventures', date: '2026-09-08T11:00:00Z', status: 'pending', size: '0.7 MB', caseRef: 'KYC-1046' },
    { id: 'DOC-707', name: 'Utility Bill - Aug 2026', type: 'Address Proof', uploadedBy: 'Blue Nile Holdings', date: '2026-09-07T09:30:00Z', status: 'approved', size: '0.5 MB', caseRef: 'KYC-1048' },
  ],
  auditTrail: [
    { id: 'AUD-001', timestamp: '2026-09-10T10:32:00Z', action: 'KYC Approved', user: 'Mary Njeri', details: 'First-leg approval completed. Risk: Low.', caseRef: 'KYC-1044' },
    { id: 'AUD-002', timestamp: '2026-09-10T10:15:00Z', action: 'CDS Card Signed', user: 'Mary Njeri', details: 'Maker second signature applied to CDS card.', caseRef: 'CDS-301' },
    { id: 'AUD-003', timestamp: '2026-09-10T09:45:00Z', action: 'Screening Cleared', user: 'Mary Njeri', details: 'PEP alert cleared. Match score 45 - false positive.', caseRef: 'SCR-504' },
    { id: 'AUD-004', timestamp: '2026-09-10T09:20:00Z', action: 'Document Reviewed', user: 'Mary Njeri', details: 'Passport verified against investor records.', caseRef: 'DOC-701' },
    { id: 'AUD-005', timestamp: '2026-09-09T16:50:00Z', action: 'KYC Rejected', user: 'Mary Njeri', details: 'Incomplete documentation. Source of funds missing.', caseRef: 'KYC-1045' },
    { id: 'AUD-006', timestamp: '2026-09-09T15:30:00Z', action: 'Screening Escalated', user: 'Mary Njeri', details: 'Sanctions match escalated to checker for review.', caseRef: 'SCR-505' },
    { id: 'AUD-007', timestamp: '2026-09-09T14:00:00Z', action: 'KYC Info Requested', user: 'Mary Njeri', details: 'Additional bank statements requested from investor.', caseRef: 'KYC-1043' },
    { id: 'AUD-008', timestamp: '2026-09-09T11:20:00Z', action: 'Document Uploaded', user: 'System', details: 'Board Resolution uploaded by Geneva Wealth Management.', caseRef: 'DOC-704' },
    { id: 'AUD-009', timestamp: '2026-09-08T16:00:00Z', action: 'KYC Approved', user: 'Mary Njeri', details: 'First-leg approval completed. Risk: Low.', caseRef: 'KYC-1047' },
    { id: 'AUD-010', timestamp: '2026-09-08T14:30:00Z', action: 'Screening Flagged', user: 'Mary Njeri', details: 'Sanctions match flagged for further investigation.', caseRef: 'SCR-502' },
  ],
};

// FORMAT HELPERS
const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '\u2014';
const formatDateTime = (d) => {
  if (!d) return '\u2014';
  const dt = new Date(d);
  return `${dt.toLocaleDateString()} ${dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

// STATUS BADGE HELPER
const getStatusBadge = (status) => {
  const style = getStatusStyle(status);
  return {
    className: 'inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border rounded-none',
    style: { ...style, borderColor: style.color },
  };
};

// RISK BADGE HELPER
const getRiskBadge = (level) => {
  const riskStyles = {
    low: { backgroundColor: '#eef1ff', color: '#2f29c7', borderColor: '#2f29c7' },
    moderate: { backgroundColor: '#e4e4e7', color: '#3f3f46', borderColor: '#3f3f46' },
    high: { backgroundColor: '#fee2e2', color: '#991b1b', borderColor: '#991b1b' },
  };
  const style = riskStyles[level?.toLowerCase()] || riskStyles.low;
  return {
    className: 'inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border rounded-none',
    style,
  };
};

// MATCH SCORE BADGE
const getScoreBadge = (score) => {
  let style;
  if (score >= 80) {
    style = { backgroundColor: '#fee2e2', color: '#991b1b' };
  } else if (score >= 50) {
    style = { backgroundColor: '#e4e4e7', color: '#3f3f46' };
  } else {
    style = { backgroundColor: '#eef1ff', color: '#2f29c7' };
  }
  return {
    className: 'inline-flex items-center px-2 py-0.5 text-[10px] font-mono tracking-wider rounded-none font-bold',
    style,
  };
};

// SHARED STYLES
const btnClass = 'flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-none text-[10px] font-mono uppercase hover:bg-zinc-50 transition-colors';
const thClass = 'text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500';

// STAT CARD COMPONENT
const StatCard = ({ label, value, icon: Icon, trend }) => (
  <div className="bg-white border border-zinc-200 rounded-none p-6">
    <div className="flex items-center justify-between mb-2">
      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{label}</span>
      <Icon className="w-4 h-4 text-zinc-400" />
    </div>
    <p className="text-2xl font-bold text-black">{value}</p>
    {trend && <p className="text-xs text-zinc-500 mt-1">{trend}</p>}
  </div>
);

// URL PATH TO TAB MAPPING
const getTabFromPath = (pathname) => {
  if (pathname.includes('/cds')) return 'cds_signing';
  if (pathname.includes('/screening')) return 'screening';
  if (pathname.includes('/documents')) return 'documents';
  if (pathname.includes('/audit')) return 'audit';
  if (pathname.includes('/kyc')) return 'kyc_queue';
  return 'kyc_queue';
};

// MAIN COMPLIANCE MAKER DASHBOARD
const ComplianceMakerDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Data state
  const [metrics, setMetrics] = useState(demoData.metrics);
  const [kycQueue, setKycQueue] = useState(demoData.kycQueue);
  const [cdsCards, setCdsCards] = useState(demoData.cdsCards);
  const [screeningAlerts, setScreeningAlerts] = useState(demoData.screeningAlerts);
  const [documents, setDocuments] = useState(demoData.documents);
  const [auditTrail, setAuditTrail] = useState(demoData.auditTrail);

  // Filters
  const [kycStatusFilter, setKycStatusFilter] = useState('all');
  const [kycRiskFilter, setKycRiskFilter] = useState('all');
  const [screeningStatusFilter, setScreeningStatusFilter] = useState('all');
  const [screeningTypeFilter, setScreeningTypeFilter] = useState('all');
  const [docSearchTerm, setDocSearchTerm] = useState('');
  const [auditSearchTerm, setAuditSearchTerm] = useState('');

  // Determine active tab from URL path
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));

  // Update tab when URL changes
  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  // TABS
  const tabs = [
    { id: 'kyc_queue', label: 'KYC Queue', path: '/bank/compliance-maker/kyc', icon: UserCheck },
    { id: 'cds_signing', label: 'CDS Signing', path: '/bank/compliance-maker/cds', icon: PenTool },
    { id: 'screening', label: 'Screening', path: '/bank/compliance-maker/screening', icon: ShieldCheck },
    { id: 'documents', label: 'Documents', path: '/bank/compliance-maker/documents', icon: FileText },
    { id: 'audit', label: 'Audit Trail', path: '/bank/compliance-maker/audit', icon: History },
  ];

  // DATA FETCHING
  const fetchData = async () => {
    const token = localStorage.getItem('forwardsflow_token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    try {
      const res = await fetch(`${API_URL}/api/v1/compliance-maker/dashboard`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (data.metrics) setMetrics(data.metrics);
        if (data.kycQueue) setKycQueue(data.kycQueue);
        if (data.cdsCards) setCdsCards(data.cdsCards);
        if (data.screeningAlerts) setScreeningAlerts(data.screeningAlerts);
        if (data.documents) setDocuments(data.documents);
        if (data.auditTrail) setAuditTrail(data.auditTrail);
      }
    } catch (err) {
      console.warn('Compliance Maker API unavailable, using demo data:', err.message);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchData();
    } finally {
      setRefreshing(false);
    }
  };

  // FILTERED DATA
  const filteredKyc = kycQueue.filter((item) => {
    if (kycStatusFilter !== 'all' && item.status !== kycStatusFilter) return false;
    if (kycRiskFilter !== 'all' && item.riskLevel !== kycRiskFilter) return false;
    return true;
  });

  const filteredScreening = screeningAlerts.filter((item) => {
    if (screeningStatusFilter !== 'all' && item.status !== screeningStatusFilter) return false;
    if (screeningTypeFilter !== 'all' && item.type !== screeningTypeFilter) return false;
    return true;
  });

  const filteredDocuments = documents.filter((doc) => {
    if (!docSearchTerm) return true;
    const term = docSearchTerm.toLowerCase();
    return doc.name.toLowerCase().includes(term) || doc.type.toLowerCase().includes(term) || doc.uploadedBy.toLowerCase().includes(term);
  });

  const filteredAudit = auditTrail.filter((entry) => {
    if (!auditSearchTerm) return true;
    const term = auditSearchTerm.toLowerCase();
    return entry.action.toLowerCase().includes(term) || entry.user.toLowerCase().includes(term) || entry.caseRef.toLowerCase().includes(term) || entry.details.toLowerCase().includes(term);
  });

  // ACTION HANDLERS
  const handleKycAction = (caseId, action) => {
    console.log(`KYC Action: ${action} on ${caseId}`);
    setKycQueue((prev) =>
      prev.map((item) => {
        if (item.id !== caseId) return item;
        if (action === 'approve') return { ...item, status: 'kyc_maker_approved' };
        if (action === 'reject') return { ...item, status: 'rejected' };
        if (action === 'request_info') return { ...item, status: 'pending_kyc' };
        return item;
      })
    );
  };

  const handleCdsSign = (cardId) => {
    console.log(`CDS Sign: ${cardId}`);
    setCdsCards((prev) =>
      prev.map((card) => {
        if (card.id !== cardId) return card;
        return { ...card, cardStatus: 'cds_card_complete', secondSigner: user?.name || 'Compliance Maker' };
      })
    );
  };

  const handleScreeningAction = (alertId, action) => {
    console.log(`Screening Action: ${action} on ${alertId}`);
    setScreeningAlerts((prev) =>
      prev.map((alert) => {
        if (alert.id !== alertId) return alert;
        if (action === 'clear') return { ...alert, status: 'cleared' };
        if (action === 'flag') return { ...alert, status: 'flagged' };
        if (action === 'escalate') return { ...alert, status: 'escalated' };
        return alert;
      })
    );
  };

  // LOADING STATE
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-zinc-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-zinc-600 mt-4">Loading compliance maker dashboard...</p>
        </div>
      </div>
    );
  }

  // RENDER
  return (
    <div className="p-6 bg-zinc-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-zinc-600 to-zinc-800 rounded-none p-6 text-white mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-6 h-6" />
              <span className="text-zinc-200 text-sm font-medium">COMPLIANCE MAKER</span>
            </div>
            <h1 className="text-2xl font-bold">{user?.name || 'Compliance Maker'}</h1>
            <p className="text-zinc-100 mt-1">
              {metrics.pendingReviews} pending reviews &bull; {metrics.pendingCds} CDS cards awaiting signature &bull; {metrics.screeningAlerts} screening alerts
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-zinc-200">Approved Today</p>
            <p className="text-3xl font-bold">{metrics.approvedToday}</p>
            <p className="text-sm text-zinc-200 mt-1">avg {metrics.avgReviewTime} per case</p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2 mb-6 bg-white rounded-none p-1 shadow-sm overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => navigate(tab.path)}
            className={`flex items-center gap-2 px-4 py-2 rounded-none font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id ? 'bg-zinc-600 text-white' : 'text-zinc-600 hover:bg-zinc-100'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.id === 'kyc_queue' && metrics.pendingReviews > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-zinc-500 text-white text-xs rounded-none">
                {metrics.pendingReviews}
              </span>
            )}
            {tab.id === 'screening' && metrics.screeningAlerts > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-zinc-500 text-white text-xs rounded-none">
                {metrics.screeningAlerts}
              </span>
            )}
          </button>
        ))}
        <div className="flex-1" />
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 text-zinc-600 hover:bg-zinc-100 rounded-none"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* KYC QUEUE TAB */}
      {activeTab === 'kyc_queue' && (
        <div className="space-y-6">
          {/* Stat Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Pending Reviews"
              value={metrics.pendingReviews}
              icon={Clock}
              trend="Awaiting first-leg review"
            />
            <StatCard
              label="Approved Today"
              value={metrics.approvedToday}
              icon={CheckCircle}
              trend="Advanced to checker"
            />
            <StatCard
              label="Rejected Today"
              value={metrics.rejectedToday}
              icon={XCircle}
              trend="Sent back to investor"
            />
            <StatCard
              label="Avg Review Time"
              value={metrics.avgReviewTime}
              icon={BarChart3}
              trend="Per KYC application"
            />
          </div>

          {/* KYC Queue Table */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">KYC Applications</h3>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Filter className="w-3.5 h-3.5 text-zinc-400" />
                  <select
                    value={kycStatusFilter}
                    onChange={(e) => setKycStatusFilter(e.target.value)}
                    className="border border-zinc-200 rounded-none text-xs px-2 py-1.5 font-mono focus:outline-none focus:border-zinc-400"
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="kyc_maker_approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="pending_kyc">Info Requested</option>
                  </select>
                </div>
                <select
                  value={kycRiskFilter}
                  onChange={(e) => setKycRiskFilter(e.target.value)}
                  className="border border-zinc-200 rounded-none text-xs px-2 py-1.5 font-mono focus:outline-none focus:border-zinc-400"
                >
                  <option value="all">All Risk Levels</option>
                  <option value="low">Low</option>
                  <option value="moderate">Moderate</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className={thClass}>Case ID</th>
                    <th className={thClass}>Investor Name</th>
                    <th className={thClass}>Type</th>
                    <th className={thClass}>Submission Date</th>
                    <th className={thClass}>Risk Level</th>
                    <th className={thClass}>Status</th>
                    <th className={thClass}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredKyc.map((item) => (
                    <tr key={item.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 font-mono text-xs">{item.id}</td>
                      <td className="py-3 px-4">
                        <div>
                          <p className="text-sm font-medium text-zinc-900">{item.investorName}</p>
                          <p className="text-[10px] text-zinc-400 font-mono">{item.country}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-600">{item.type}</span>
                      </td>
                      <td className="py-3 px-4 text-xs text-zinc-500">{formatDate(item.submissionDate)}</td>
                      <td className="py-3 px-4">
                        <span {...getRiskBadge(item.riskLevel)}>{item.riskLevel}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span {...getStatusBadge(item.status)}>{item.status.replace(/_/g, ' ')}</span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => console.log('Review', item.id)}
                            className={btnClass}
                            title="Review case"
                          >
                            <Eye className="w-3 h-3" />
                            Review
                          </button>
                          {item.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleKycAction(item.id, 'approve')}
                                className={btnClass}
                                title="Approve and advance to checker"
                              >
                                <CheckCircle className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => handleKycAction(item.id, 'reject')}
                                className={btnClass}
                                title="Reject application"
                              >
                                <XCircle className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => handleKycAction(item.id, 'request_info')}
                                className={btnClass}
                                title="Request more information"
                              >
                                <MessageSquare className="w-3 h-3" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredKyc.length === 0 && (
                <div className="text-center py-8 text-zinc-400">
                  <Inbox className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">No KYC applications match the current filters.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CDS SIGNING TAB */}
      {activeTab === 'cds_signing' && (
        <div className="space-y-6">
          {/* Stat Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Awaiting 2nd Signature"
              value={cdsCards.filter((c) => c.cardStatus === 'cds_card_checker_signed').length}
              icon={PenTool}
              trend="Ready for maker signature"
            />
            <StatCard
              label="Pending Cards"
              value={cdsCards.filter((c) => c.cardStatus === 'cds_card_pending').length}
              icon={Clock}
              trend="No signatures yet"
            />
            <StatCard
              label="Completed"
              value={cdsCards.filter((c) => c.cardStatus === 'cds_card_complete').length}
              icon={CheckCircle}
              trend="Both signatures applied"
            />
            <StatCard
              label="Total Cards"
              value={cdsCards.length}
              icon={ClipboardList}
              trend="All CDS cards"
            />
          </div>

          {/* CDS Cards */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">CDS Cards Awaiting Signature</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {cdsCards.map((card) => {
                const canSign = card.cardStatus === 'cds_card_checker_signed';
                return (
                  <div key={card.id} className="border border-zinc-200 rounded-none p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-mono text-xs text-zinc-900 font-bold">{card.id}</span>
                      <span {...getStatusBadge(card.cardStatus)}>{card.cardStatus.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="space-y-2 mb-4">
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">Investor</p>
                        <p className="text-sm text-zinc-900 font-medium">{card.investorName}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">Bank</p>
                          <p className="text-sm text-zinc-700">{card.bank}</p>
                        </div>
                        <div>
                          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">Account</p>
                          <p className="text-xs text-zinc-700 font-mono">{card.accountNumber}</p>
                        </div>
                      </div>
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">CSD Reference</p>
                        <p className="text-xs text-zinc-700 font-mono">{card.csdRef}</p>
                      </div>
                      {card.firstSigner && (
                        <div className="border-t border-zinc-100 pt-2">
                          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">1st Signature</p>
                          <p className="text-sm text-zinc-700">{card.firstSigner}</p>
                          <p className="text-[10px] text-zinc-400">{formatDateTime(card.firstSignedAt)}</p>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleCdsSign(card.id)}
                      disabled={!canSign}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-none text-xs font-mono uppercase tracking-wider transition-colors ${
                        canSign
                          ? 'bg-zinc-700 text-white hover:bg-zinc-800'
                          : 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                      }`}
                    >
                      <PenTool className="w-3.5 h-3.5" />
                      {canSign ? 'Apply 2nd Signature' : card.cardStatus === 'cds_card_complete' ? 'Completed' : 'Awaiting 1st Signature'}
                    </button>
                  </div>
                );
              })}
            </div>
            {cdsCards.length === 0 && (
              <div className="text-center py-8 text-zinc-400">
                <PenTool className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">No CDS cards to display.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* SCREENING TAB */}
      {activeTab === 'screening' && (
        <div className="space-y-6">
          {/* Stat Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Pending Alerts"
              value={screeningAlerts.filter((a) => a.status === 'pending').length}
              icon={AlertTriangle}
              trend="Awaiting adjudication"
            />
            <StatCard
              label="Cleared"
              value={screeningAlerts.filter((a) => a.status === 'cleared').length}
              icon={CheckCircle}
              trend="False positives resolved"
            />
            <StatCard
              label="Flagged"
              value={screeningAlerts.filter((a) => a.status === 'flagged').length}
              icon={Flag}
              trend="Under investigation"
            />
            <StatCard
              label="Escalated"
              value={screeningAlerts.filter((a) => a.status === 'escalated').length}
              icon={ArrowUpRight}
              trend="Sent to checker"
            />
          </div>

          {/* Screening Alerts Table */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Screening Alerts</h3>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Filter className="w-3.5 h-3.5 text-zinc-400" />
                  <select
                    value={screeningStatusFilter}
                    onChange={(e) => setScreeningStatusFilter(e.target.value)}
                    className="border border-zinc-200 rounded-none text-xs px-2 py-1.5 font-mono focus:outline-none focus:border-zinc-400"
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="cleared">Cleared</option>
                    <option value="flagged">Flagged</option>
                    <option value="escalated">Escalated</option>
                  </select>
                </div>
                <select
                  value={screeningTypeFilter}
                  onChange={(e) => setScreeningTypeFilter(e.target.value)}
                  className="border border-zinc-200 rounded-none text-xs px-2 py-1.5 font-mono focus:outline-none focus:border-zinc-400"
                >
                  <option value="all">All Types</option>
                  <option value="PEP">PEP</option>
                  <option value="Sanctions">Sanctions</option>
                  <option value="Adverse Media">Adverse Media</option>
                </select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className={thClass}>Alert ID</th>
                    <th className={thClass}>Investor</th>
                    <th className={thClass}>Type</th>
                    <th className={thClass}>Matched Entity</th>
                    <th className={thClass}>Source</th>
                    <th className={`${thClass} text-center`}>Match Score</th>
                    <th className={thClass}>Status</th>
                    <th className={thClass}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredScreening.map((alert) => (
                    <tr key={alert.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 font-mono text-xs">{alert.id}</td>
                      <td className="py-3 px-4 text-sm font-medium text-zinc-900">{alert.investorName}</td>
                      <td className="py-3 px-4">
                        <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-600">{alert.type}</span>
                      </td>
                      <td className="py-3 px-4 text-xs text-zinc-600">{alert.matchedEntity}</td>
                      <td className="py-3 px-4 text-xs text-zinc-500">{alert.source}</td>
                      <td className="py-3 px-4 text-center">
                        <span {...getScoreBadge(alert.matchScore)}>{alert.matchScore}%</span>
                      </td>
                      <td className="py-3 px-4">
                        <span {...getStatusBadge(alert.status)}>{alert.status}</span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          {alert.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleScreeningAction(alert.id, 'clear')}
                                className={btnClass}
                                title="Clear alert"
                              >
                                <CheckCircle className="w-3 h-3" />
                                Clear
                              </button>
                              <button
                                onClick={() => handleScreeningAction(alert.id, 'flag')}
                                className={btnClass}
                                title="Flag for investigation"
                              >
                                <Flag className="w-3 h-3" />
                                Flag
                              </button>
                              <button
                                onClick={() => handleScreeningAction(alert.id, 'escalate')}
                                className={btnClass}
                                title="Escalate to checker"
                              >
                                <ArrowUpRight className="w-3 h-3" />
                              </button>
                            </>
                          )}
                          {alert.status !== 'pending' && (
                            <button
                              onClick={() => console.log('View', alert.id)}
                              className={btnClass}
                            >
                              <Eye className="w-3 h-3" />
                              View
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredScreening.length === 0 && (
                <div className="text-center py-8 text-zinc-400">
                  <ShieldCheck className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">No screening alerts match the current filters.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DOCUMENTS TAB */}
      {activeTab === 'documents' && (
        <div className="space-y-6">
          {/* Stat Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Documents to Review"
              value={metrics.documentsToReview}
              icon={FileText}
              trend="Pending review"
            />
            <StatCard
              label="Approved"
              value={documents.filter((d) => d.status === 'approved').length}
              icon={FileCheck}
              trend="Verified and accepted"
            />
            <StatCard
              label="Rejected"
              value={documents.filter((d) => d.status === 'rejected').length}
              icon={XCircle}
              trend="Returned to investor"
            />
            <StatCard
              label="Total Documents"
              value={documents.length}
              icon={ClipboardList}
              trend="All uploaded documents"
            />
          </div>

          {/* Documents Table */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Document Management</h3>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input
                    type="text"
                    placeholder="Search documents..."
                    value={docSearchTerm}
                    onChange={(e) => setDocSearchTerm(e.target.value)}
                    className="pl-9 pr-4 py-2 border border-zinc-200 rounded-none text-sm focus:outline-none focus:border-zinc-400"
                  />
                </div>
                <button className="flex items-center gap-2 px-3 py-2 border border-zinc-200 rounded-none text-xs font-mono uppercase hover:bg-zinc-50 transition-colors">
                  <Upload className="w-3.5 h-3.5" />
                  Upload
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className={thClass}>Name</th>
                    <th className={thClass}>Type</th>
                    <th className={thClass}>Uploaded By</th>
                    <th className={thClass}>Date</th>
                    <th className={thClass}>Case Ref</th>
                    <th className={thClass}>Size</th>
                    <th className={thClass}>Status</th>
                    <th className={thClass}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.map((doc) => (
                    <tr key={doc.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-zinc-900">{doc.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-600">{doc.type}</span>
                      </td>
                      <td className="py-3 px-4 text-sm text-zinc-700">{doc.uploadedBy}</td>
                      <td className="py-3 px-4 text-xs text-zinc-500">{formatDate(doc.date)}</td>
                      <td className="py-3 px-4 font-mono text-xs text-zinc-600">{doc.caseRef}</td>
                      <td className="py-3 px-4 text-xs text-zinc-500 font-mono">{doc.size}</td>
                      <td className="py-3 px-4">
                        <span {...getStatusBadge(doc.status)}>{doc.status}</span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => console.log('Review doc', doc.id)}
                            className={btnClass}
                            title="Review document"
                          >
                            <Eye className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => console.log('Tag doc', doc.id)}
                            className={btnClass}
                            title="Tag document"
                          >
                            <Tag className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => console.log('Download doc', doc.id)}
                            className={btnClass}
                            title="Download document"
                          >
                            <Download className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredDocuments.length === 0 && (
                <div className="text-center py-8 text-zinc-400">
                  <FileText className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">No documents match the current search.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AUDIT TRAIL TAB */}
      {activeTab === 'audit' && (
        <div className="space-y-6">
          {/* Stat Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Actions Today"
              value={auditTrail.filter((e) => {
                const d = new Date(e.timestamp);
                const today = new Date();
                return d.toDateString() === today.toDateString();
              }).length}
              icon={History}
              trend="Compliance actions logged"
            />
            <StatCard
              label="KYC Actions"
              value={auditTrail.filter((e) => e.action.toLowerCase().includes('kyc')).length}
              icon={UserCheck}
              trend="Approvals, rejections, info requests"
            />
            <StatCard
              label="Screening Actions"
              value={auditTrail.filter((e) => e.action.toLowerCase().includes('screening')).length}
              icon={ShieldCheck}
              trend="Clears, flags, escalations"
            />
            <StatCard
              label="Total Entries"
              value={auditTrail.length}
              icon={ClipboardList}
              trend="All audit trail records"
            />
          </div>

          {/* Audit Trail Table */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Audit Trail</h3>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input
                    type="text"
                    placeholder="Search audit trail..."
                    value={auditSearchTerm}
                    onChange={(e) => setAuditSearchTerm(e.target.value)}
                    className="pl-9 pr-4 py-2 border border-zinc-200 rounded-none text-sm focus:outline-none focus:border-zinc-400"
                  />
                </div>
                <button className="flex items-center gap-2 px-3 py-2 border border-zinc-200 rounded-none text-xs font-mono uppercase hover:bg-zinc-50 transition-colors">
                  <Download className="w-3.5 h-3.5" />
                  Export
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className={thClass}>Timestamp</th>
                    <th className={thClass}>Action</th>
                    <th className={thClass}>User</th>
                    <th className={thClass}>Details</th>
                    <th className={thClass}>Case Ref</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAudit.map((entry) => {
                    // Determine action icon
                    let ActionIcon = History;
                    if (entry.action.includes('Approved')) ActionIcon = CheckCircle;
                    else if (entry.action.includes('Rejected')) ActionIcon = XCircle;
                    else if (entry.action.includes('Cleared')) ActionIcon = CheckCircle;
                    else if (entry.action.includes('Flagged')) ActionIcon = Flag;
                    else if (entry.action.includes('Escalated')) ActionIcon = ArrowUpRight;
                    else if (entry.action.includes('Signed')) ActionIcon = PenTool;
                    else if (entry.action.includes('Reviewed')) ActionIcon = Eye;
                    else if (entry.action.includes('Uploaded')) ActionIcon = Upload;
                    else if (entry.action.includes('Tagged')) ActionIcon = Tag;
                    else if (entry.action.includes('Info Requested')) ActionIcon = MessageSquare;

                    return (
                      <tr key={entry.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                        <td className="py-3 px-4 text-xs text-zinc-500 font-mono whitespace-nowrap">{formatDateTime(entry.timestamp)}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <ActionIcon className="w-3.5 h-3.5 text-zinc-500" />
                            <span className="text-sm font-medium text-zinc-900">{entry.action}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-zinc-700">{entry.user}</td>
                        <td className="py-3 px-4 text-xs text-zinc-600 max-w-xs truncate" title={entry.details}>{entry.details}</td>
                        <td className="py-3 px-4 font-mono text-xs text-zinc-600">{entry.caseRef}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredAudit.length === 0 && (
                <div className="text-center py-8 text-zinc-400">
                  <History className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">No audit trail entries match the current search.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceMakerDashboard;
